import { Bundle, DocumentReference, Resource } from "@medplum/fhirtypes";
import { cloneDeep } from "lodash";

/**
 * Filters out FHIR resources based on One Medical DocumentReference analysis.
 *
 * Process:
 * 1. Find DocumentReferences with "One Medical" in contained field
 * 2. Extract attachment titles from those DocumentReferences
 * 3. Remove other resources that match those titles in meta.source or extension.valueString
 * 4. Keep DocumentReferences intact
 */
export function filterOneMedicalResources(bundle: Bundle): Bundle {
  if (!bundle.entry?.length) return bundle;

  // Create a deep copy to avoid mutating the original bundle
  const filteredBundle = cloneDeep(bundle);

  // Step 1: Find DocumentReferences with "One Medical" in contained field
  const oneMedicalDocRefs = findOneMedicalDocumentReferences(filteredBundle);

  // Step 2: Extract attachment titles from those DocumentReferences
  const attachmentTitles = extractAttachmentTitles(oneMedicalDocRefs);

  // Step 3: Filter out resources that match those titles
  if (attachmentTitles.length > 0) {
    filteredBundle.entry = filteredBundle.entry?.filter(entry => {
      if (!entry.resource) return true;

      // Never remove DocumentReferences
      if (entry.resource.resourceType === "DocumentReference") {
        return true;
      }

      // Check if this resource should be filtered out
      return !shouldFilterResource(entry.resource, attachmentTitles);
    });
  }

  return filteredBundle;
}

/**
 * Finds DocumentReferences that contain "One Medical" in their contained field
 */
function findOneMedicalDocumentReferences(bundle: Bundle): DocumentReference[] {
  const oneMedicalDocRefs: DocumentReference[] = [];

  bundle.entry?.forEach(entry => {
    if (entry.resource?.resourceType === "DocumentReference") {
      const docRef = entry.resource as DocumentReference;

      // Check contained resources for "One Medical" organization
      if (docRef.contained) {
        const hasOneMedical = docRef.contained.some(contained => {
          if (contained.resourceType === "Organization") {
            const org = contained as { name?: string };
            return org.name?.toLowerCase().includes("one medical");
          }
          return false;
        });

        if (hasOneMedical) {
          oneMedicalDocRefs.push(docRef);
        }
      }
    }
  });

  return oneMedicalDocRefs;
}

/**
 * Extracts attachment titles from DocumentReferences
 */
function extractAttachmentTitles(documentReferences: DocumentReference[]): string[] {
  const titles: string[] = [];

  documentReferences.forEach(docRef => {
    if (docRef.content?.[0]?.attachment?.title) {
      titles.push(docRef.content[0].attachment.title);
    }
  });

  return titles;
}

/**
 * Determines if a resource should be filtered out based on attachment titles
 */
function shouldFilterResource(resource: Resource, attachmentTitles: string[]): boolean {
  // Check meta.source
  if (resource.meta?.source) {
    if (attachmentTitles.some(title => resource.meta?.source?.includes(title))) {
      return true;
    }
  }

  // Check extensions for valueString
  const resourceWithExtensions = resource as { extension?: Array<{ valueString?: string }> };
  if (resourceWithExtensions.extension) {
    for (const ext of resourceWithExtensions.extension) {
      if (ext.valueString) {
        if (attachmentTitles.some(title => ext.valueString?.includes(title))) {
          return true;
        }
      }
    }
  }

  return false;
}
